import { useState, useEffect, createContext, useContext } from "react";
import { Index, InstantSearch } from "react-instantsearch";
import TypesenseInstantSearchAdapter from "typesense-instantsearch-adapter";
import { ConstantContext } from "./ConstantProvider";

export const TypesenseContext = createContext(undefined);

export const TypesenseProvider = ({ children }) => {
  const [searchClient, setSearchClient] = useState(undefined);
  const { environmentVariables } = useContext(ConstantContext);

  useEffect(() => {
    let typesenseEnv = environmentVariables["TYPESENSE_ENV"];
    let adapter = new TypesenseInstantSearchAdapter({
      server: {
        apiKey: environmentVariables["TYPESENSE_API_KEY"],
        nodes: [
          {
            host: environmentVariables["TYPESENSE_HOST"],
            port: environmentVariables["TYPESENSE_PORT"],
            protocol:
              environmentVariables["TYPESENSE_PORT"] == 443 ? "https" : "http",
          },
        ],
        connectionTimeoutSeconds: 1,
        numRetries: 3,
      },
      collectionSpecificSearchParameters: {
        [`${typesenseEnv}_company`]: {
          query_by: [
            "best_name",
            "best_domain",
            "all_names_and_domains",
            "description",
            "investors",
            "sector_tags.value_facet",
            "sector_tags.key_words",
            "business_model_tags.value_facet",
            "business_model_tags.key_words",
            "deal_flows.name_facet",
            "deal_flows.investment_status_facet",
            "deal_flows.max_investment_status_facet",
            "headquarter_address.country_facet",
            "headquarter_address.locality_facet",
            "headquarter_address.administrative_area_level_1_facet",
            "investment_round_facet",
            "deal_source",
            "impact",
            "equity_story",
            "management_data",
            "deal_owner.email",
            "deal_owner.first_name",
            "deal_owner.last_name",
            "secondary_owner.email",
            "secondary_owner.last_name",
            "secondary_owner.first_name",
            "secondary_owner.username",
            "impact_model_tags.value_facet",
            "mapping_and_certification_tags.value_facet",
          ].join(","),
          per_page: 78,
        },
        [`${typesenseEnv}_people`]: {
          query_by:
            "best_name,best_email_address,all_emails,all_names,tags_sectors.value_facet,tags_sectors.key_words,tags_business_models.value_facet,tags_business_models.key_words",
          per_page: 78,
        },
        [`${typesenseEnv}_note`]: {
          query_by:
            "subject,content_text,domains,emails,owner_email,owner_username,owner_full_name_facet",
          query_by_weights: "127,10,10,10,10,10,10",
          per_page: 78,
          snippet_threshold: 200,
        },
      },
    });
    // monkey patch to resolve the problem of index containing the env.
    adapter.searchClient.typesenseEnv = typesenseEnv;
    setSearchClient(adapter.searchClient);
  }, []);

  if (!searchClient) return <></>;

  return (
    <TypesenseContext.Provider value={searchClient}>
      {children}
    </TypesenseContext.Provider>
  );
};

const TypesenseInstantSearch = ({ children, indexName, ...props }) => {
  const searchClient = useContext(TypesenseContext);
  const { environmentVariables } = useContext(ConstantContext);
  const typesenseEnv = environmentVariables["TYPESENSE_ENV"];

  if (indexName) {
    indexName = `${typesenseEnv}_${indexName}`;
  }

  return (
    <InstantSearch
      searchClient={searchClient}
      indexName={indexName}
      {...props}
      future={{
        preserveSharedStateOnUnmount: true,
      }}
    >
      {children}
    </InstantSearch>
  );
};

export const TypesenseIndex = ({ children, indexName }) => {
  const { environmentVariables } = useContext(ConstantContext);
  const typesenseEnv = environmentVariables["TYPESENSE_ENV"];

  if (indexName) {
    indexName = `${typesenseEnv}_${indexName}`;
  }
  return <Index indexName={indexName}>{children}</Index>;
};

export default TypesenseInstantSearch;
